// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ProfileRequestType } from '../../types/profileRequest';

/**
 * Cardinality of the policy the advisor must attach to a Request of a given
 * type. `EXACTLY_ONE` → the wizard shows a Policy dropdown and requires a
 * selection. `NONE` → the wizard hides the dropdown.
 */
export type PolicyCardinality = 'NONE' | 'EXACTLY_ONE';

/**
 * Declarative description of one additional input the advisor must fill in
 * for a specific Request type. Consumed by `TypeSpecificParamsRenderer` to
 * build the form row beneath the type + policy dropdowns.
 *
 * The MVP registry has no types with additional fields (both LOA and CoA
 * derive everything server-side). Kept as part of the shape so future
 * types — e.g. a Fund Switch request — can plug in without a component
 * refactor.
 */
export interface FieldSpec {
  readonly key: string;
  readonly label: string;
  readonly required: boolean;
  readonly kind: 'text' | 'number' | 'select';
  readonly options?: ReadonlyArray<{ value: string; text: string }>;
}

/**
 * Cardinality of the client the advisor must attach to a Request of a given
 * type. `EXACTLY_ONE` → the wizard shows a Client dropdown and requires a
 * selection (Open Banking Connection — sent to the chosen client only).
 * `NONE` → the request goes to the profile's primary client by default.
 *
 * Distinct from {@link PolicyCardinality}: a Request may be profile-only
 * (no policy) and still client-scoped, or per-policy (one policy) without
 * an explicit client pick.
 */
export type ClientCardinality = 'NONE' | 'EXACTLY_ONE';

export interface RequestTypeSpec {
  /** Human-readable label shown in the type dropdown. */
  readonly label: string;
  readonly policyCardinality: PolicyCardinality;
  readonly clientCardinality: ClientCardinality;
  readonly additionalFields: ReadonlyArray<FieldSpec>;
}

/**
 * Partial because only types with a backend handler are exposed in the
 * wizard today (LOA + CoA + Open Banking Connection). Future types
 * (Fact-find, KYC, …) land here as their handlers ship.
 */
export const requestTypeRegistry: Partial<Record<ProfileRequestType, RequestTypeSpec>> = {
  LOA: {
    label: 'Letter of Authority',
    policyCardinality: 'EXACTLY_ONE',
    clientCardinality: 'NONE',
    additionalFields: [],
  },
  COA: {
    label: 'Change of Agency',
    policyCardinality: 'EXACTLY_ONE',
    clientCardinality: 'NONE',
    additionalFields: [],
  },
  OPEN_BANKING_CONNECTION: {
    // BRD-locked label — see docs/specs/open-banking-connection-request-brd.md §4.1.
    label: 'Open Banking Connection',
    // Profile-scoped (BR-OBC-003), zero policies. Client-scoped (BR-OBC-002):
    // the card hides the policy dropdown and renders a Client picker instead.
    policyCardinality: 'NONE',
    clientCardinality: 'EXACTLY_ONE',
    additionalFields: [],
  },
  FACT_FIND: {
    // Profile-scoped, no policy, no explicit client picker — the action is
    // assigned server-side to the profile's primary client. The client
    // completes the Request through the conversational fact-find chatbot
    // (intelligence-service, FACT_FIND use case).
    label: 'Fact Finding',
    policyCardinality: 'NONE',
    clientCardinality: 'NONE',
    additionalFields: [],
  },
};

export type SupportedRequestType = Extract<ProfileRequestType, keyof typeof requestTypeRegistry>;

export const supportedRequestTypes: SupportedRequestType[] =
  (Object.keys(requestTypeRegistry) as SupportedRequestType[]);

export function getTypeSpec(type: ProfileRequestType): RequestTypeSpec | undefined {
  return requestTypeRegistry[type];
}
